// src/components/Layout.js (Versão com Header Mobile)

import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import WhatsNewModal from './WhatsNewModal';

const CURRENT_APP_VERSION = '2.0.0';
const CHANGELOG = {
  '2.0.0': [
    '📎 Comprovantes em Receitas e Despesas! Anexe PDFs e imagens diretamente nos seus lançamentos.',
    '☁️ Armazenamento seguro na nuvem — seus arquivos protegidos e sempre disponíveis.',
    '🔍 Acesse, baixe ou remova comprovantes a qualquer momento direto no lançamento.',
    '⚡ Performance e estabilidade: melhorias internas para uma experiência mais rápida.',
    '❤️ Obrigado por fazer parte do FinControl!'
  ]
};

function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/app/transactions?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const lastSeenVersion = localStorage.getItem('appVersion');
    if (lastSeenVersion !== CURRENT_APP_VERSION) {
      setIsWhatsNewOpen(true);
    }

    // Sincronizar status da assinatura com o Asaas
    const syncSubscription = async () => {
      const userObj = JSON.parse(localStorage.getItem('user') || '{}');
      // Só sincroniza se tem assinatura pendente (plan existe mas status não é ACTIVE)
      if (userObj.plan && userObj.subscription_status !== 'ACTIVE') {
        try {
          const { default: api } = await import('../services/api');
          const response = await api.post('/subscriptions/sync');
          if (response.data.is_premium) {
            userObj.subscription_status = 'ACTIVE';
            localStorage.setItem('user', JSON.stringify(userObj));
            window.location.reload();
          }
        } catch (err) {
          console.log('Sync check:', err.message);
        }
      }
    };
    syncSubscription();
  }, []);

  const handleCloseWhatsNew = () => {
    localStorage.setItem('appVersion', CURRENT_APP_VERSION);
    setIsWhatsNewOpen(false);
  };


  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user?.first_name ? `${user.first_name} ${user.last_name || ''}` : 'Usuário';

  return (
    <div className="bg-surface text-on-surface h-screen flex overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 flex flex-col md:ml-64 h-screen relative overflow-y-auto w-full">
        {location.pathname !== '/app/transactions' && (
        <header className="sticky top-0 z-30 bg-slate-50/80 backdrop-blur-md flex justify-between items-center px-4 md:px-8 h-16 shadow-sm shrink-0">
          <div className="flex items-center space-x-4">
            <button className="md:hidden text-slate-600 hover:text-indigo-500 transition-colors" onClick={() => setIsSidebarOpen(true)}>
              <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>menu</span>
            </button>
            <div className="relative hidden sm:block">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-xl">search</span>
              <input
                type="text"
                placeholder="Buscar transações..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearch}
                className="bg-surface-container-high border-none rounded-full pl-10 pr-4 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 w-64 transition-all"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4 md:space-x-6">
            <div className="relative" ref={notifRef}>
              <button
                className="relative text-slate-600 hover:text-indigo-500 transition-colors"
                onClick={() => setIsNotifOpen(prev => !prev)}
              >
                <span className="material-symbols-outlined">notifications</span>
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-error rounded-full"></span>
              </button>

              {isNotifOpen && (
                <div className="absolute right-0 top-10 w-80 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <h4 className="text-sm font-bold text-slate-900">Notificações</h4>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-indigo-50 px-2 py-0.5 rounded-full">Novo</span>
                  </div>
                  <div className="divide-y divide-slate-50">
                    <div className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-indigo-50 text-primary flex items-center justify-center shrink-0 mt-0.5">
                        <span className="material-symbols-outlined text-[16px]">auto_awesome</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">FinControl Premium</p>
                        <p className="text-xs text-slate-500 mt-0.5">Desbloqueie Cartões e Imposto de Renda com o plano Premium.</p>
                        <p className="text-[10px] text-slate-400 mt-1 font-medium">Agora</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-green-50 text-green-600 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="material-symbols-outlined text-[16px]">tips_and_updates</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Dica do dia</p>
                        <p className="text-xs text-slate-500 mt-0.5">Use relatórios mensais para identificar gastos excessivos por categoria.</p>
                        <p className="text-[10px] text-slate-400 mt-1 font-medium">Hoje</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 border-t border-slate-100 text-center">
                    <button className="text-xs font-bold text-primary hover:text-indigo-800 transition-colors" onClick={() => setIsNotifOpen(false)}>
                      Ver todas as notificações
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button className="text-slate-600 hover:text-indigo-500 transition-colors hidden sm:block">
              <span className="material-symbols-outlined">settings</span>
            </button>
            
            <div className="flex items-center space-x-3 pl-4 border-l border-slate-200">
              <div className="text-right hidden md:block">
                <p className="text-sm font-semibold text-on-surface">{userName}</p>
                <p className="text-[10px] text-secondary">{user?.subscription_status === 'ACTIVE' ? 'Premium Client' : 'Standard Client'}</p>
              </div>
              <div className="w-8 h-8 rounded-full border-2 border-primary-fixed shadow-sm bg-primary-container text-white flex items-center justify-center font-bold text-xs uppercase">
                {userName.charAt(0)}
              </div>
            </div>
          </div>
        </header>
        )}

        <section className="flex-1 px-4 md:px-8 pt-6 pb-12 overflow-x-hidden">
          <Outlet />
        </section>

        <WhatsNewModal
          isOpen={isWhatsNewOpen}
          onClose={handleCloseWhatsNew}
          version={CURRENT_APP_VERSION}
          changes={CHANGELOG[CURRENT_APP_VERSION]}
        />

        <footer className="w-full border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row justify-between items-center px-4 md:px-12 py-6 font-inter text-xs text-slate-500 gap-4 mt-auto">
          <p>&copy; 2024 FinControl Editorial. All rights reserved.</p>
          <div className="flex space-x-4 md:space-x-6">
            <a href="#" className="hover:text-slate-800 hover:underline transition-all">Privacy Policy</a>
            <a href="#" className="hover:text-slate-800 hover:underline transition-all">Terms of Service</a>
            <a href="#" className="hover:text-slate-800 hover:underline transition-all">Security</a>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default Layout;